import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import {
  uiSchema as epcUiSchema,
  getSchema as getEpcSchema,
} from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo";
import {
  IUiSchema,
  IUiSchemaDropdownSelectField,
  IUiSchemaNumberField,
  IUiSchemaSegmentedControl,
  IUiSchemaTextField,
} from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { getValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import {
  EElectricityType,
  ENaturalGasAvailable,
  EYesNoUnknown,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { JSONSchema6 } from "json-schema";
import { FormValidation } from "react-native-jsonschema-form-test";
import { validationErrorsForMediaUrl } from "utils/mediaUrl.utils";
import { EAN_MASK } from "./schema.editPropertyExtraInfo.config";
import {
  optionsForSet,
  TOptionSet,
} from "./schema.editPropertyExtraInfo.options";
import {
  EExtraInfoGroup,
  EField,
  IExtraData,
} from "./schema.editPropertyExtraInfo.types";
import {
  propertyTypeCanHaveAdjoinedType,
  propertyTypeCanHaveFloors,
  validationErrorsForEan,
} from "./schema.editPropertyExtraInfo.utils";

const booleanOptions = [
  {
    value: getLocalizedText("system.no"),
    id: "no",
  },
  {
    value: getLocalizedText("system.yes"),
    id: "yes",
  },
];

const hasElectricity = (formData: any) =>
  formData[EField.ElectricityType] &&
  formData[EField.ElectricityType] !== EElectricityType.Unknown;

const hasNaturalGas = (formData: any) =>
  formData[EField.NaturalGasAvailable] === ENaturalGasAvailable.Available;

const hasGarden = (formData: any) => formData[EField.HasGarden] === "yes";

const hasTerrace = (formData: any) => formData[EField.HasTerrace] === "yes";

/** One field of one group. `options` names an enum list, `booleans` makes it a
 * yes/no control, and `showIfProperty` is for the two questions that depend on
 * the property's type rather than on what is filled in beside them. */
export interface IGroupField {
  field: EField;
  options?: TOptionSet;
  booleans?: boolean;
  maxLength?: number;
  required?: boolean;
  showIf?: (formData: any) => boolean;
  showIfProperty?: (extraData: IExtraData) => boolean | undefined;
  requiredIf?: (formData: any) => boolean;
}

// The cards of the extra tab, each one a list of fields in the order they are
// asked. The nine groups a candidate sees on the Rentmatch property page are
// mirrored here field for field, so what a broker fills in and what a
// candidate reads are the same list under the same headings.
export const GROUP_FIELDS: { [key in EExtraInfoGroup]?: IGroupField[] } = {
  [EExtraInfoGroup.General]: [
    { field: EField.Price },
    { field: EField.Costs },
    { field: EField.CostsComment },
    { field: EField.AvailableAtDate },
    { field: EField.RoomCountBedroom },
    { field: EField.RoomCountBathroom },
    { field: EField.LivingSurfaceArea },
    { field: EField.BuildingFloor, showIfProperty: propertyTypeCanHaveFloors },
    {
      field: EField.BuildingFloorCount,
      showIfProperty: propertyTypeCanHaveFloors,
    },
  ],

  [EExtraInfoGroup.Bookkeeping]: [
    { field: EField.CostUnit, maxLength: 8 },
    { field: EField.BookkeepingPaidOutStartDate },
  ],

  [EExtraInfoGroup.BookkeepingGroup]: [
    { field: EField.CostCenter, maxLength: 8 },
  ],

  [EExtraInfoGroup.Electricity]: [
    {
      field: EField.ElectricityType,
      options: "electricityType",
      required: true,
    },
    { field: EField.ElectricityEan, showIf: hasElectricity },
    { field: EField.ElectricityMeterNumberBoth, showIf: hasElectricity },
    {
      field: EField.ElectricityMeterType,
      options: "electricityMeterType",
      showIf: hasElectricity,
      requiredIf: hasElectricity,
    },
  ],

  [EExtraInfoGroup.NaturalGas]: [
    {
      field: EField.NaturalGasAvailable,
      options: "naturalGasAvailable",
      required: true,
    },
    { field: EField.NaturalGasEan, showIf: hasNaturalGas },
    { field: EField.NaturalGasMeterNumber, showIf: hasNaturalGas },
    {
      field: EField.NaturalGasMeterType,
      options: "naturalGasMeterType",
      showIf: hasNaturalGas,
      requiredIf: hasNaturalGas,
    },
  ],

  [EExtraInfoGroup.Water]: [{ field: EField.WaterMeterNumber, required: true }],

  // The surface and the orientation only appear once the answer is yes. A
  // property without a garden has no garden surface to ask about, and leaving
  // both visible is what fills a listing with zeros.
  [EExtraInfoGroup.Exterior]: [
    { field: EField.GroundSurfaceArea },
    { field: EField.BuildingSurfaceArea },
    { field: EField.HasGarden, booleans: true },
    { field: EField.BuildingGardenSurfaceArea, showIf: hasGarden },
    {
      field: EField.GardenOrientation,
      options: "orientation",
      showIf: hasGarden,
    },
    { field: EField.HasTerrace, booleans: true },
    { field: EField.BuildingTerraceSurfaceArea, showIf: hasTerrace },
    {
      field: EField.TerraceOrientation,
      options: "orientation",
      showIf: hasTerrace,
    },
    { field: EField.HasGardenShed, booleans: true },
    { field: EField.HasPool, booleans: true },
    { field: EField.NumberOfParkingsOutside },
  ],

  [EExtraInfoGroup.Interior]: [
    { field: EField.NumberOfToilets },
    { field: EField.NumberOfShowerRooms },
    { field: EField.HasAttic, booleans: true },
    { field: EField.BuildingHasStorage, booleans: true },
    { field: EField.IsFurnished, booleans: true },
    { field: EField.KitchenType, options: "kitchenType" },
    { field: EField.FloorType, options: "floorType" },
    { field: EField.BuildingHasHardMaterials, booleans: true },
    { field: EField.HasOven, booleans: true },
    { field: EField.HasFridge, booleans: true },
    { field: EField.HasDishWasher, booleans: true },
    { field: EField.HasRangeHood, booleans: true },
    { field: EField.RoomCountGarage },
    { field: EField.NumberOfParkingsInside },
  ],

  [EExtraInfoGroup.Comfort]: [
    { field: EField.HasLift, booleans: true },
    { field: EField.HasAirConditioning, booleans: true },
    { field: EField.HasAlarm, booleans: true },
    { field: EField.HasVideophone, booleans: true },
    { field: EField.HasSmokeDetectors, booleans: true },
    { field: EField.AccessibleForDisabled, options: "yesNoUnknown" },
  ],

  [EExtraInfoGroup.Energy]: [
    { field: EField.HasCentralHeating, booleans: true },
    { field: EField.HeatingType, options: "heatingType" },
    { field: EField.HeatingDistribution, options: "heatingDistribution" },
    { field: EField.HasDoubleGlazing, booleans: true },
    { field: EField.GlazingType, options: "glazingType" },
    { field: EField.WindowFrameMaterial, options: "windowFrameMaterial" },
    { field: EField.HasInsulation, booleans: true },
    { field: EField.HasSolarPanels, booleans: true },
    { field: EField.IsLowEnergy, booleans: true },
    { field: EField.IsPassiveHouse, booleans: true },
    { field: EField.Co2Emission },
  ],

  [EExtraInfoGroup.HouseRules]: [
    { field: EField.PetsAllowed, options: "yesNoUnknown" },
    {
      field: EField.PetsAllowedDetail,
      options: "petsAllowedDetail",
      showIf: (formData: any) =>
        formData[EField.PetsAllowed] === EYesNoUnknown.Yes,
    },
    { field: EField.SmokingAllowed, options: "yesNoUnknown" },
  ],

  [EExtraInfoGroup.Condition]: [
    { field: EField.Condition, options: "condition" },
    { field: EField.ConstructionYear },
    { field: EField.RenovationYear },
    { field: EField.EnvironmentType, options: "environmentType" },
  ],

  [EExtraInfoGroup.Structural]: [
    {
      field: EField.BuildingAdjoined,
      options: "adjoined",
      showIfProperty: propertyTypeCanHaveAdjoinedType,
    },
    { field: EField.FrontWidth },
    { field: EField.Volume },
    { field: EField.RoofType, options: "roofType" },
    { field: EField.RoofMaterial, options: "roofMaterial" },
  ],

  [EExtraInfoGroup.Risk]: [
    { field: EField.FloodZoneGScore, options: "floodScore" },
    { field: EField.FloodZonePScore, options: "floodScore" },
    { field: EField.ConformityCertificate, options: "yesNoUnknown" },
    { field: EField.ElectricityCertificate, options: "electricityCertificate" },
    { field: EField.AsbestosCertificate },
    { field: EField.AsbestosCertificateDate },
  ],

  [EExtraInfoGroup.Media]: [
    { field: EField.VirtualTourUrl },
    { field: EField.VideoUrl },
    { field: EField.Model3dUrl },
    { field: EField.FloorPlanUrl },
  ],
};

const buildGroupSchema = (
  groupFields: IGroupField[],
  initialValues: { [key: string]: any },
  defaultValues: { [key: string]: any },
  extraData: IExtraData,
): JSONSchema6 => {
  const properties: { [key: string]: any } = {};

  groupFields.forEach(groupField => {
    const {
      field,
      options,
      booleans,
      maxLength,
      showIf,
      showIfProperty,
      requiredIf,
    } = groupField;

    const canShow = showIfProperty ? showIfProperty(extraData) : undefined;

    properties[field] = {
      default: getValue(field, initialValues, defaultValues),
      ...(options ? { options: optionsForSet(options) } : {}),
      ...(booleans ? { options: booleanOptions } : {}),
      ...(maxLength ? { maxLength } : {}),
      ...(showIf ? { showIf } : {}),
      ...(showIfProperty ? { showIf: () => canShow } : {}),
      ...(requiredIf ? { requiredIf } : {}),
    };
  });

  return {
    required: groupFields
      .filter(groupField => groupField.required)
      .map(groupField => groupField.field),
    properties,
  } as JSONSchema6;
};

export const getSchema =
  (
    initialValues: {
      [key: string]: any;
    },
    extraData: IExtraData,
  ) =>
  (defaultValues: { [key: string]: any }): JSONSchema6 => {
    // The EPC card is the one group that starts from a schema of its own: the
    // label, the score, the expiry and the certificate number are asked by the
    // shared EPC form, and the two EPB levels are added beside them.
    if (extraData.extraInfoGroup === EExtraInfoGroup.Epc) {
      const epcSchema = getEpcSchema(
        { property: extraData.property, epcIsRequired: false },
        initialValues,
      )(defaultValues);

      return {
        ...epcSchema,
        properties: {
          ...epcSchema.properties,
          [EField.ELevel]: {
            default: getValue(EField.ELevel, initialValues, defaultValues),
          },
          [EField.KLevel]: {
            default: getValue(EField.KLevel, initialValues, defaultValues),
          },
        },
      };
    }

    const groupFields = GROUP_FIELDS[extraData.extraInfoGroup];
    if (!groupFields) {
      return {
        required: [],
        properties: {},
      };
    }

    return buildGroupSchema(
      groupFields,
      initialValues,
      defaultValues,
      extraData,
    );
  };

// Every row of this tab is one of four shapes. Written once, because the
// tab asks about seventy of them and a hand-written entry each time is where
// a wrong field type slips in.
const numberField = (
  titleKey: string,
  extra?: Partial<IUiSchemaNumberField<EField>>,
): IUiSchemaNumberField<EField> => ({
  "ui:field": ERentioFormField.Number,
  title: getLocalizedText(titleKey),
  optional: true,
  ...extra,
});

const textField = (
  titleKey: string,
  extra?: Partial<IUiSchemaTextField<EField>>,
): IUiSchemaTextField<EField> => ({
  "ui:field": ERentioFormField.Text,
  title: getLocalizedText(titleKey),
  optional: true,
  ...extra,
});

const yesNoField = (titleKey: string): IUiSchemaSegmentedControl<EField> => ({
  "ui:field": ERentioFormField.SegmentedControl,
  title: getLocalizedText(titleKey),
  optional: true,
});

const selectField = (
  titleKey: string,
): IUiSchemaDropdownSelectField<EField> => ({
  "ui:field": ERentioFormField.DropdownSelect,
  title: getLocalizedText(titleKey),
  optional: true,
});

export const uiSchema = (
  initialValues: {
    [key: string]: any;
  },
  extraData: IExtraData,
): IUiSchema<EField> => ({
  "rentio:navigationTitle": getLocalizedText(
    `property.extra.${extraData.extraInfoGroup}.edit`.toLowerCase(),
  ),

  // General
  [EField.Price]: numberField("property_info.form.price.label", {
    formatSummary: (formData?: number) => formData && formatCurrency(formData),
    placeholder: "0",
    suffixText: "EUR",
    isFloat: true,
  }),
  [EField.Costs]: numberField("property_info.form.costs.label", {
    formatSummary: (formData?: number) => formData && formatCurrency(formData),
    placeholder: "0",
    suffixText: "EUR",
    isFloat: true,
  }),
  [EField.CostsComment]: textField("property.field.costs_comment"),
  [EField.AvailableAtDate]: {
    "ui:field": ERentioFormField.Date,
    title: getLocalizedText("property_info.form.available_at_date.label"),
    optional: true,
  },
  [EField.RoomCountBedroom]: numberField(
    "property.extra.interior.field.room_count_bedroom",
  ),
  [EField.RoomCountBathroom]: numberField(
    "property.extra.interior.field.room_count_bathroom",
  ),
  [EField.LivingSurfaceArea]: numberField(
    "property.extra.interior.field.living_surface_area",
    { suffixText: "m²", isFloat: true },
  ),
  [EField.BuildingFloor]: numberField(
    "property.extra.interior.field.building_floor",
  ),
  [EField.BuildingFloorCount]: numberField(
    "property.extra.interior.field.building_layers",
  ),

  // EPC
  ...epcUiSchema({ epcIsRequired: false }, initialValues),
  [EField.ELevel]: numberField("property.field.e_level"),
  [EField.KLevel]: numberField("property.field.k_level"),

  // Media. Plain text rather than a url field: what is pasted here comes from
  // a viewer, a video host or a floor-plan tool, and refusing something that
  // does not parse would keep a broker from storing a link that works. What is
  // shown is decided where it is read, which only renders an http(s) address.
  [EField.VirtualTourUrl]: textField("property.extra.media.item.virtual_tour"),
  [EField.VideoUrl]: textField("property.extra.media.item.video"),
  [EField.Model3dUrl]: textField("property.extra.media.item.model_3d"),
  [EField.FloorPlanUrl]: textField("property.extra.media.item.floor_plan"),

  // Bookkeeping
  [EField.CostUnit]: {
    "ui:field": ERentioFormField.Text,
    title: getLocalizedText("property.extra.bookkeeping.item.cost_unit.title"),
  },
  [EField.BookkeepingPaidOutStartDate]: {
    "ui:field": ERentioFormField.Date,
    title: getLocalizedText(
      "property.extra.bookkeeping.item.bookkeeping_paid_out_start_date.title",
    ),
  },

  // Bookkeeping group
  [EField.CostCenter]: {
    "ui:field": ERentioFormField.Text,
    title: getLocalizedText(
      "property.extra.bookkeeping.item.cost_center.title",
    ),
  },

  // Electricity
  [EField.ElectricityType]: {
    "ui:field": ERentioFormField.InlineSelect,
    title: getLocalizedText("move.field.electricity_type.title"),
  },
  [EField.ElectricityEan]: textField("move.field.electricity_ean.title", {
    iMaskProps: {
      mask: EAN_MASK,
      lazy: false,
      unmask: true,
      placeholderChar: ". ",
    },
  }),
  [EField.ElectricityMeterNumberBoth]: textField(
    "move.field.electricity_meter_number_both.title",
  ),
  [EField.ElectricityMeterType]: {
    "ui:field": ERentioFormField.InlineSelect,
    title: getLocalizedText("move.field.electricity_meter_type.title"),
  },

  // Natural Gas
  [EField.NaturalGasAvailable]: {
    "ui:field": ERentioFormField.InlineSelect,
    title: getLocalizedText("move.field.natural_gas_type.title"),
  },
  [EField.NaturalGasEan]: textField("move.field.natural_gas_ean.title", {
    iMaskProps: {
      mask: EAN_MASK,
      lazy: false,
      unmask: true,
      placeholderChar: ". ",
    },
  }),
  [EField.NaturalGasMeterNumber]: textField(
    "move.field.natural_gas_meter_number.title",
  ),
  [EField.NaturalGasMeterType]: {
    "ui:field": ERentioFormField.InlineSelect,
    title: getLocalizedText("move.field.natural_gas_meter_type.title"),
  },

  // Water
  [EField.WaterMeterNumber]: textField("move.field.water_meter_number.title"),

  // Exterior
  [EField.GroundSurfaceArea]: numberField(
    "property.field.ground_surface_area",
    { suffixText: "m²", isFloat: true },
  ),
  [EField.BuildingSurfaceArea]: numberField(
    "property.extra.exterior.field.building_surface_area",
    { suffixText: "m²", isFloat: true },
  ),
  [EField.HasGarden]: yesNoField("property.field.has_garden"),
  [EField.BuildingGardenSurfaceArea]: numberField(
    "property.extra.exterior.field.building_garden_surface_area",
    { suffixText: "m²", isFloat: true },
  ),
  [EField.GardenOrientation]: selectField("property.field.garden_orientation"),
  [EField.HasTerrace]: yesNoField("property.field.has_terrace"),
  [EField.BuildingTerraceSurfaceArea]: numberField(
    "property.extra.exterior.field.building_terrace_surface_area",
    { suffixText: "m²", isFloat: true },
  ),
  [EField.TerraceOrientation]: selectField(
    "property.field.terrace_orientation",
  ),
  [EField.HasGardenShed]: yesNoField("property.field.has_garden_shed"),
  [EField.HasPool]: yesNoField("property.field.has_pool"),
  [EField.NumberOfParkingsOutside]: numberField(
    "property.field.number_of_parkings_outside",
  ),

  // Interior
  [EField.NumberOfToilets]: numberField("property.field.number_of_toilets"),
  [EField.NumberOfShowerRooms]: numberField(
    "property.field.number_of_shower_rooms",
  ),
  [EField.HasAttic]: yesNoField("property.field.has_attic"),
  [EField.BuildingHasStorage]: yesNoField(
    "property.extra.interior.field.building_has_storage",
  ),
  [EField.IsFurnished]: yesNoField(
    "property.extra.interior.field.is_furnished",
  ),
  [EField.KitchenType]: selectField("property.field.kitchen_type"),
  [EField.FloorType]: selectField("property.field.floor_type"),
  [EField.BuildingHasHardMaterials]: yesNoField(
    "property.field.building_has_hard_materials",
  ),
  [EField.HasOven]: yesNoField("property.extra.amenities.item.has_oven"),
  [EField.HasFridge]: yesNoField("property.extra.amenities.item.has_fridge"),
  [EField.HasDishWasher]: yesNoField(
    "property.extra.amenities.item.has_dish_washer",
  ),
  [EField.HasRangeHood]: yesNoField(
    "property.extra.amenities.item.has_range_hood",
  ),
  [EField.RoomCountGarage]: numberField(
    "property.extra.interior.field.room_count_garage",
  ),
  [EField.NumberOfParkingsInside]: numberField(
    "property.field.number_of_parkings_inside",
  ),

  // Comfort
  [EField.HasLift]: yesNoField("property.extra.amenities.item.has_lift"),
  [EField.HasAirConditioning]: yesNoField(
    "property.field.has_air_conditioning",
  ),
  [EField.HasAlarm]: yesNoField("property.field.has_alarm"),
  [EField.HasVideophone]: yesNoField("property.field.has_videophone"),
  [EField.HasSmokeDetectors]: yesNoField("property.field.has_smoke_detectors"),
  [EField.AccessibleForDisabled]: selectField(
    "property.field.accessible_for_disabled",
  ),

  // Energy
  [EField.HasCentralHeating]: yesNoField(
    "property.extra.energy.item.has_central_heating",
  ),
  [EField.HeatingType]: selectField(
    "property.extra.energy.item.has_heating_type",
  ),
  [EField.HeatingDistribution]: selectField(
    "property.field.heating_distribution",
  ),
  [EField.HasDoubleGlazing]: yesNoField(
    "property.extra.energy.item.has_double_glazing",
  ),
  [EField.GlazingType]: selectField("property.field.glazing_type"),
  [EField.WindowFrameMaterial]: selectField(
    "property.field.window_frame_material",
  ),
  [EField.HasInsulation]: yesNoField("property.field.has_insulation"),
  [EField.HasSolarPanels]: yesNoField("property.field.has_solar_panels"),
  [EField.IsLowEnergy]: yesNoField("property.field.is_low_energy"),
  [EField.IsPassiveHouse]: yesNoField("property.field.is_passive_house"),
  [EField.Co2Emission]: numberField("property.field.co2_emission", {
    suffixText: "kg CO₂/m²",
    isFloat: true,
  }),

  // House rules
  [EField.PetsAllowed]: selectField("property.field.pets_allowed"),
  [EField.PetsAllowedDetail]: selectField("property.field.pets_allowed_detail"),
  [EField.SmokingAllowed]: selectField("property.field.smoking_allowed"),

  // Condition and surroundings
  [EField.Condition]: selectField("property.field.condition"),
  [EField.ConstructionYear]: numberField(
    "property.extra.general.item.construction_year",
  ),
  [EField.RenovationYear]: numberField("property.field.renovation_year"),
  [EField.EnvironmentType]: selectField("property.field.environment_type"),

  // Structural
  [EField.BuildingAdjoined]: selectField(
    "property.extra.exterior.field.building_adjoined",
  ),
  [EField.FrontWidth]: numberField("property.field.front_width", {
    suffixText: "m",
    isFloat: true,
  }),
  [EField.Volume]: numberField("property.field.volume", {
    suffixText: "m³",
    isFloat: true,
  }),
  [EField.RoofType]: selectField("property.field.roof_type"),
  [EField.RoofMaterial]: selectField("property.field.roof_material"),

  // Risk and certificates
  [EField.FloodZoneGScore]: selectField("property.field.flood_zone_g_score"),
  [EField.FloodZonePScore]: selectField("property.field.flood_zone_p_score"),
  [EField.ConformityCertificate]: selectField(
    "property.field.conformity_certificate",
  ),
  [EField.ElectricityCertificate]: selectField(
    "property.field.electricity_certificate",
  ),
  [EField.AsbestosCertificate]: textField(
    "property.field.asbestos_certificate",
  ),
  [EField.AsbestosCertificateDate]: {
    "ui:field": ERentioFormField.Date,
    title: getLocalizedText("property.field.asbestos_certificate_date"),
    optional: true,
  },
});

export const getValidate =
  (extraData: IExtraData) => (formData: any, errors: FormValidation) => {
    switch (extraData.extraInfoGroup) {
      case EExtraInfoGroup.Electricity: {
        validationErrorsForEan(formData[EField.ElectricityEan]).forEach(
          error => {
            errors[EField.ElectricityEan].addError(error);
          },
        );

        break;
      }

      case EExtraInfoGroup.NaturalGas: {
        validationErrorsForEan(formData[EField.NaturalGasEan]).forEach(
          error => {
            errors[EField.NaturalGasEan].addError(error);
          },
        );

        break;
      }

      case EExtraInfoGroup.Media: {
        // Refused here rather than sanitized on the way out: what is stored is
        // read by three screens and by whatever reads it next, and only one of
        // them is ours today.
        [
          EField.VirtualTourUrl,
          EField.VideoUrl,
          EField.Model3dUrl,
          EField.FloorPlanUrl,
        ].forEach(field => {
          validationErrorsForMediaUrl(formData[field]).forEach(error => {
            errors[field].addError(error);
          });
        });

        break;
      }
    }

    return errors;
  };
